// The Certified badge - four states, a real state machine (docs/design/
// slice4-certify-ux.md §6.4; ADR-0020 decision 6). `BadgeState` is landed
// (packages/shared/src/model/certify.ts:200-201) and is a PURE, DERIVED, server-
// computed value (packages/certify/src/badge.ts `deriveBadgeState`) - this component
// only ever DISPLAYS the state it is given, never recomputes a transition (keep-
// derived-state-acyclic, app-engineering §2; this client has no history to derive
// `dulled` vs `certified_current` from safely). Every state gets its own alt text
// naming the actual state (never a generic "Nightcrew badge" alt for all four,
// `color-not-only`/`alt-text`) plus a visible text label alongside the graphic.
import type { BadgeState } from "@nightcrew/shared";
import { certifyCopy, type CertifyCopyKey } from "../copy/certify.js";

const ALT_KEY: Record<BadgeState, CertifyCopyKey> = {
  certified_current: "certify.badge.sealAlt.certified_current",
  dulled: "certify.badge.sealAlt.dulled",
  unverified: "certify.badge.sealAlt.unverified",
  expired: "certify.badge.sealAlt.expired",
};

const LABEL_KEY: Record<BadgeState, CertifyCopyKey> = {
  certified_current: "certify.badge.label.certified_current",
  dulled: "certify.badge.label.dulled",
  unverified: "certify.badge.label.unverified",
  expired: "certify.badge.label.expired",
};

/** The seal artwork's per-state treatment (ux spec §6.4): `certified_current` is the
 * full-color locked reference art; `dulled` is the SAME artwork desaturated at reduced
 * opacity (never a different mark); `unverified` is outline-only (never embedded
 * publicly, management-preview only); `expired` is the outline shape plus an explicit
 * "Expired" tag overlay (never silently reverting to the `unverified` look). */
const Seal = ({ state }: { readonly state: BadgeState }): React.JSX.Element => {
  const outlineOnly = state === "unverified" || state === "expired";
  const dulled = state === "dulled";
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 44 44"
      aria-hidden="true"
      className={`certified-seal${dulled ? " dulled" : ""}${outlineOnly ? " outline" : ""}`}
    >
      <circle
        cx="22"
        cy="22"
        r="19"
        fill={outlineOnly ? "none" : "var(--ink)"}
        stroke={outlineOnly ? "var(--ink-3)" : "var(--amber)"}
        strokeWidth={outlineOnly ? 1.75 : 3}
      />
      {!outlineOnly ? <circle cx="22" cy="22" r="14" fill="none" stroke="var(--amber)" strokeWidth="2" /> : null}
      {state === "expired" ? (
        <line x1="10" y1="34" x2="34" y2="10" stroke="var(--risk)" strokeWidth="2" />
      ) : null}
    </svg>
  );
};

export const CertifiedBadge = ({
  state,
  appName,
  href,
}: {
  readonly state: BadgeState;
  readonly appName: string;
  /** The canonical public trust-page URL to link to. Present only once the state is
   * actually public-facing; `unverified` is never linked (§6.4 - "never actually
   * embedded on a founder's own site"). */
  readonly href?: string;
}): React.JSX.Element => {
  const alt = certifyCopy(ALT_KEY[state], { appName });
  const label = certifyCopy(LABEL_KEY[state]);
  const body = (
    <span className="row aic gap8 badge-with-label" data-testid="certified-badge" data-badge-state={state}>
      <Seal state={state} />
      <span className="sr-only">{alt}</span>
      <b aria-hidden="true" style={{ fontSize: 13 }}>
        {label}
      </b>
    </span>
  );
  if (href === undefined || state === "unverified") return body;
  return (
    <a href={href} className="badge-link" aria-label={alt}>
      {body}
    </a>
  );
};
